import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import InfiniteScroll from "react-infinite-scroll-component";
import parse from "html-react-parser";
import clsx from "clsx";
import { useDispatch, useSelector } from "react-redux";
import { getCurrentUser } from "../../../shared/utility";
import { cloneDeep, escapeRegExp, isEmpty, replace, uniqBy } from "lodash";
import useDidMountEffect from "../../../hooks/useDidMountEffect";
import useMenuKeyboardNav from "../../../hooks/useMenuKeyboardNav";
import { LOCAL_STORAGE_KEYS, ON_LOAD } from "../../../constants/Constants";
import { getSessionAssigneeList } from "../../../actions/sessionListAction";
import { arrayAddIfNotExistCheckWithKey, arrayElementExistCheckWithKey } from "../../../shared/arrayExtension";
import storage from "../../../services/storage";
import { Localize } from "../Localize";
import { Localize as AriaLocalize } from "../../../shared/ariaLabelLocalize";
import PublicVariables from "../../../constants/PublicVariables";
import { announceResultCount } from "../../UI/LiveRegion";

const PROFILE_URL = process.env.REACT_APP_PROFILE_URL;
const TEN_RECORDS_PER_PAGE = 10;

const HostDropDown = ({ localisedValues, onHostSelected }) => {
    const userData = useMemo(() => {
        const data = storage.get(LOCAL_STORAGE_KEYS.USER_DATA);
        return {
            ...data,
            fullName: `${data?.firstName} ${data?.lastName} (${Localize[PublicVariables.currentLanguage]?.you_lable})`,
        };
    }, []);
    const dispatch = useDispatch();
    const { getSessionAssigneeListData } = useSelector((state) => state.sessionList);
    const ariaLangId = useSelector((state) => state.language?.selectedlanguage?.id);
    const ariaLocalised = AriaLocalize[ariaLangId] || AriaLocalize['en_US'];
    const [assigneeList, setAssigneeList] = useState([]);
    const [selectedAssignee, setSelectedAssignee] = useState(userData);
    const [search, setSearch] = useState('');
    const [skip, setSkip] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const announceSearchRef = useRef(false);

    const { triggerRef, menuRef } = useMenuKeyboardNav({
        itemSelector: '[role="option"]',
        typeAheadText: (el) => el.getAttribute('data-typeahead') || el.textContent,
        listbox: true,
    });


    useEffect(() => {
        onHostSelected(userData?.userId)
        fetchUsers(ON_LOAD);
    }, []);

    useDidMountEffect(() => {
        const timer = setTimeout(() => {
            setSkip(0);
            announceSearchRef.current = true;
            fetchUsers();
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    useDidMountEffect(() => {
        if (skip !== 0) {
            fetchUsers(skip);
        }
    }, [skip]);

    const fetchUsers = (initiate = '') => {
        const req = { SKIP: skip, SEARCH: search.trim(), INITIATE: initiate };
        dispatch(getSessionAssigneeList(req))
    };

    useDidMountEffect(() => {
        if (getSessionAssigneeListData?.list) {
            const { req, list } = getSessionAssigneeListData;
            let filteredUsers = parseUsers(getSessionAssigneeListData?.list, getSessionAssigneeListData?.req);
            let currentUser = getCurrentUser();
            currentUser.fullName = currentUser.firstName + " " + currentUser.lastName + " " + `(${localisedValues["you_lable"]})`
            delete currentUser.SELECTED;

            if (!isEmpty(filteredUsers)) {
                filteredUsers.unshift(currentUser);
                filteredUsers = uniqBy(filteredUsers, 'userId');
            }

            setAssigneeList(filteredUsers);
            setHasMore(getSessionAssigneeListData?.list.length >= TEN_RECORDS_PER_PAGE);
            if (announceSearchRef.current && req?.SKIP === 0 && req?.SEARCH === search.trim()) {
                announceSearchRef.current = false;
                announceResultCount(filteredUsers.length, { langId: ariaLangId, hasMore: list.length >= TEN_RECORDS_PER_PAGE });
            }
        }
    }, [getSessionAssigneeListData]);

    const parseUsers = (users, req) => {
        let usersListCopy = req.SKIP === 0 ? [] : cloneDeep(assigneeList); // use state list copy

        if (req.INITIATE === ON_LOAD || (req.SKIP === 0 && isEmpty(req.SEARCH))) {
            const user = getCurrentUser();
            user.fullName = user.firstName + " " + user.lastName + " " + `(${localisedValues["you_lable"]})`
            delete user.SELECTED;
            usersListCopy = arrayAddIfNotExistCheckWithKey(usersListCopy, user, "userId");
        }

        users.forEach((value) => {
            if (!arrayElementExistCheckWithKey(usersListCopy, "userId", value.userId)) {
                const userCopy = cloneDeep(value);
                userCopy.fullName = `${userCopy.firstName} ${userCopy.lastName}`;
                if (!isEmpty(search)) {
                  // ✅ Escape user input to prevent ReDoS
                  const safeSearch = escapeRegExp(search.trim());
                  const regex = new RegExp(safeSearch, "i");
                  const found = userCopy.fullName.match(regex);
                  if (found) {
                    userCopy.fullName = replace(
                      userCopy.fullName,
                      regex,
                      `<b>${found[0]}</b>`
                    );
                  }
                }
                usersListCopy.push(userCopy);
            }
        });

        return usersListCopy;
    };

    const handleAssigneeChange = (user) => {
        setSelectedAssignee(user);
        onHostSelected(user?.userId);
    };

    const removeBTageAndParse = (val = '') => {
        const plainText = val.replace(/<b>/gi, "").replace(/<\/b>/gi, "");
        const youLabel = `(${localisedValues["you_lable"]})`;

        let displayText = plainText;
        if (selectedAssignee?.userId === userData?.userId && !plainText.includes(youLabel)) {
            displayText += ` ${youLabel}`;
        } else if (selectedAssignee?.userId !== userData?.userId) {
            displayText = displayText.replace(youLabel, "").trim();
        }

        return parse(displayText);
    }

    return (
        <div className="filter-box user-filter-box">
            <div className="dropdown top-dropdown">
                <button
                    className="btn btn-secondary dropdown-toggle triangle-none"
                    type="button"
                    id="host-dropdown"
                    data-bs-toggle="dropdown"
                    aria-haspopup="listbox"
                    aria-expanded="false"
                    aria-label={ariaLocalised.host_currently.replace('{value}', selectedAssignee?.fullName ? selectedAssignee.fullName.replace(/<\/?b>/g, '') : 'None')}
                    ref={triggerRef}
                >
                    <div className={clsx("user-icon", !selectedAssignee?.profileImage && "user-none-icon")}>
                        {selectedAssignee?.profileImage ? (
                            <img src={PROFILE_URL + selectedAssignee.profileImage} alt="" />
                        ) : (
                            <span className="for-icon icon-none-user" aria-hidden="true"></span>
                        )}
                    </div>
                    <div className="filter-item">
                        {selectedAssignee?.fullName ? removeBTageAndParse(selectedAssignee.fullName) : "None"}
                    </div>
                    <span className="triangle-icon icon-down-triangle-round" aria-hidden="true"></span>
                </button>

                <div className="dropdown-menu user-filter-dropdown-menu" aria-labelledby="host-dropdown" ref={menuRef}>
                    <div className="dropdown-menu-in side-padding pb-0">
                        <div className="search-form bottom-border esm">
                            <div className="form-group-in">
                                <span className="for-icon icon-search margin-0"></span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder={localisedValues["search"]}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                        </div>

                        <div>
                        <InfiniteScroll
                            dataLength={assigneeList.length}
                            next={() => setSkip(prev => prev + TEN_RECORDS_PER_PAGE)}
                            hasMore={hasMore}
                            scrollableTarget="scrollableList"
                            className="custom-scroll-div-thin"
                            height={200}
                            style={{ overflowX: "hidden", overflowY: "auto", height: "auto" }}
                        >
                            <ul className="user-filter-list" id="scrollableList" role="listbox" aria-labelledby="host-dropdown">
                                {assigneeList.length === 0 && search !== '' ? (
                                    <li role="presentation"><span style={{ fontWeight: "600", fontSize: '13px' }}>{localisedValues["no_user_found_lable"]}</span></li>
                                ) : (
                                    assigneeList.map((item) => {
                                        const isSelected = selectedAssignee?.userId === item.userId;
                                        const plainFullName = (item.fullName || '').replace(/<b>/gi, '').replace(/<\/b>/gi, '');
                                        return (
                                            <li
                                                key={item.userId}
                                                className={clsx(isSelected && "active")}
                                                role="presentation"
                                            >
                                                <button
                                                    type="button"
                                                    className="btn-unstyled dropdown-item"
                                                    onClick={() => handleAssigneeChange(item)}
                                                    role="option"
                                                    aria-selected={isSelected}
                                                    tabIndex={-1}
                                                    data-typeahead={plainFullName}
                                                >
                                                    <div className="dropdown-item-box">
                                                        <div className="user-icon">
                                                            <img src={PROFILE_URL + item.profileImage} alt="" />
                                                        </div>
                                                        <div className="user-name">{parse(item.fullName)}</div>
                                                        {isSelected && <span className="visually-hidden">, {ariaLocalised.selected}</span>}
                                                    </div>
                                                </button>
                                            </li>
                                        );
                                    })
                                )}
                            </ul>
                        </InfiniteScroll>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

HostDropDown.propTypes = {
    localisedValues: PropTypes.objectOf(PropTypes.string),
    onHostSelected: PropTypes.func,
};

export default HostDropDown;
